import { z } from 'zod';
/** How the call reached the assistant, or left it. */
export var AssistantVoiceCallType;
(function (AssistantVoiceCallType) {
    AssistantVoiceCallType["PhoneInbound"] = "PHONE_INBOUND";
    AssistantVoiceCallType["PhoneOutbound"] = "PHONE_OUTBOUND";
    AssistantVoiceCallType["WebCall"] = "WEB_CALL";
    AssistantVoiceCallType["Other"] = "OTHER";
})(AssistantVoiceCallType || (AssistantVoiceCallType = {}));
/** The telephony vendor that carried the call. */
export var AssistantVoiceCallProvider;
(function (AssistantVoiceCallProvider) {
    AssistantVoiceCallProvider["Vapi"] = "VAPI";
    AssistantVoiceCallProvider["Twilio"] = "TWILIO";
})(AssistantVoiceCallProvider || (AssistantVoiceCallProvider = {}));
/** One line of what the telephony vendor estimated the call cost it, in its own currency. */
export const AssistantVoiceCallCostSchema = z.object({
    currency: z.string(),
    amount: z.number(),
    precision: z.number().nullish(),
});
/**
 * One voice call: who it was between, when it ran, how it ended, and what the assistant made of
 * it. The words spoken are the chat messages of the session it belongs to, not fields here.
 *
 * The vendor payloads are deliberately left out — the assistant configuration sent to the
 * telephony vendor, and the call metadata carrying SIP headers and transfer state. Both are
 * unbounded, keyed by the vendor, and answer nothing about the conversation.
 */
export const AssistantVoiceCallSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    sessionId: z.uuid().describe('The chat session whose messages are this call transcript.'),
    type: z.enum(AssistantVoiceCallType),
    provider: z.enum(AssistantVoiceCallProvider),
    providerCallId: z.string().nullable(),
    toPhoneNumber: z.string(),
    fromPhoneNumber: z.string(),
    callStartTime: z.iso.datetime().nullable(),
    callEndTime: z.iso.datetime().nullable(),
    endedReason: z
        .string()
        .nullable()
        .describe('Why the call stopped, as reported by the telephony vendor.'),
    summary: z.string().nullable().describe('The assistant-written summary of the conversation.'),
    successEvaluation: z
        .string()
        .nullable()
        .describe("The assistant's own verdict on whether the call achieved what it set out to do."),
    callCost: z
        .record(z.string(), AssistantVoiceCallCostSchema.optional())
        .nullable()
        .describe('What the telephony vendor estimated the call cost it, per line item. This is the vendor’s own estimate of its cost, not what Notable charges a customer for the call, and the two must never be reported as the same number.'),
    assistantDefinitionId: z
        .uuid()
        .nullable()
        .describe('The version of the voice assistant configuration that answered.'),
    assistantName: z.string().nullable().describe('The display name of that voice assistant.'),
    assistantPhoneNumber: z.string().nullable(),
    assistantProvider: z.enum(AssistantVoiceCallProvider).nullable(),
    assistantCallId: z.string().nullable(),
    practiceId: z
        .uuid()
        .nullable()
        .describe('The practice the answering assistant belonged to when the call was placed.'),
    gsPath: z.string().nullable().describe('Where the call recording is stored.'),
});
export const AssistantVoiceCallParamsSchema = z.object({
    voiceCallId: z.uuid(),
});
//# sourceMappingURL=voiceCall.js.map